import React, { useState, useRef, useCallback, useEffect } from 'react'
import { TextField, Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { debounce } from 'lodash'

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
    '& fieldset': {
      borderColor: theme.palette.grey[300],
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },
  '& .MuiInputBase-input': {
    resize: 'vertical',
  },
}))

const CounterText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isNearLimit' && prop !== 'isOverLimit',
})(({ theme, isNearLimit, isOverLimit }) => {
  let color = theme.palette.text.secondary
  if (isOverLimit) {
    color = theme.palette.error.main
  } else if (isNearLimit) {
    color = theme.palette.warning.main
  }

  return {
    textAlign: 'right',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    fontSize: '0.75rem',
    color,
    display: 'block',
    width: '100%',
  }
})

/**
 * TextareaWithCounter component that prevents excessive Formik re-renders
 * by debouncing the onChange callback while keeping the UI responsive.
 */
export const TextareaWithCounter = ({
  maxValue,
  label,
  name,
  value = '',
  onChange,
  onBlur,
  disabled = false,
  error,
  helperText,
  rows = 4,
  className,
  debounceTime = 300,
  ...rest
}) => {
  // Local state for immediate UI updates
  const [localValue, setLocalValue] = useState(value || '')

  // Debounced onChange ref
  const debouncedOnChange = useRef(debounce((event) => onChange(event), debounceTime))

  // Sync local state with prop value when it changes externally
  useEffect(() => {
    setLocalValue(value || '')
  }, [value])

  // Cleanup debounce on unmount
  useEffect(
    () => () => {
      debouncedOnChange.current.cancel()
    },
    [],
  )

  const handleChange = useCallback(
    (event) => {
      const newValue = event.target.value
      setLocalValue(newValue) // Update local state immediately for responsive UI

      // Create a new event with the current value for Formik
      const debouncedEvent = {
        ...event,
        target: {
          ...event.target,
          name,
          value: newValue,
        },
      }

      debouncedOnChange.current(debouncedEvent)
    },
    [name],
  )

  const currentLength = localValue?.length || 0
  const isNearLimit = maxValue && currentLength > maxValue * 0.8
  const isOverLimit = maxValue && currentLength > maxValue

  return (
    <Box className={className}>
      <StyledTextField
        label={label}
        name={name}
        value={localValue}
        onChange={handleChange}
        onBlur={onBlur}
        disabled={disabled}
        error={error || isOverLimit}
        helperText={helperText}
        multiline
        rows={rows}
        fullWidth
        variant="outlined"
        inputProps={{
          maxLength: maxValue,
        }}
        {...rest}
      />
      {maxValue && (
        <CounterText variant="caption" isNearLimit={isNearLimit} isOverLimit={isOverLimit}>
          {currentLength}/{maxValue}
        </CounterText>
      )}
    </Box>
  )
}
